/* ------------------------------------------- */
/*   Actions for creating work info items
/* ------------------------------------------- */

import { createWorkInfoItems } from 'AppSrc/core/work-info-items/work-info-item-queries';


/* --- Actions Types --- */

// Create (POST) CRQ to API
const CREATE_WORK_INFO_ITEMS_REQUEST = 'CREATE_WORK_INFO_ITEMS_REQUEST';
const CREATE_WORK_INFO_ITEMS_SUCCESS = 'CREATE_WORK_INFO_ITEMS_SUCCESS';
const CREATE_WORK_INFO_ITEMS_ERROR = 'CREATE_WORK_INFO_ITEMS_ERROR';
const CRQ_WORK_ITEMS_FETCHED = 'CRQ_WORK_ITEMS_FETCHED';
const WORK_INFO_ITEM_ATTACHMENT_READ = 'WORK_INFO_ITEM_ATTACHMENT_READ';


/* ------ ACTION CREATORS ------ */

/* - create CRQ synchronous actions - */

const createWorkInfoItemsRequest = (crqId) => ({
  type: CREATE_WORK_INFO_ITEMS_REQUEST,
  payload: {
    crqId,
  },
});

const createWorkInfoItemsSuccess = (crqId, workInfoItems) => ({
  type: CREATE_WORK_INFO_ITEMS_SUCCESS,
  payload: {
    crqId,
    workInfoItems,
  },
});

const createWorkInfoItemsError = (crqId, error) => ({
  type: CREATE_WORK_INFO_ITEMS_ERROR,
  payload: {
    crqId,
    error,
  },
});

const workInfoItemAttachmentRead = (fileId) => ({
  type: WORK_INFO_ITEM_ATTACHMENT_READ,
  payload: {
    fileId,
  },
});

const crqWorkItemsFetched = (crqId, workInfoItems) => ({
  type: CRQ_WORK_ITEMS_FETCHED,
  payload: {
    crqId,
    workInfoItems,
  },
});


// get the relevant fields out of the wii for comparison
// this allows us to ignore the following fields
// id - can change between requests for new wiis
// attachment - is null for existing wiis but filled in for new ones
// view_access and locked - returned by getCrq but unused
// requester is sent up but not returned by the endpoint
const standardiseWii = (wii) => ({
  crqId: wii.crq_id ? wii.crq_id : null,
  id: wii.id ? wii.id : null,
  infoType: wii.info_type ? wii.info_type : null,
  network: wii.network ? wii.network : null,
  source: wii.soure ? wii.network : null,
  summary: wii.summary ? wii.summary : '',
  notes: wii.notes ? wii.notes : '',
  requester: null, // There is a ticket to remove requestor from wii
});

// indicate if a wii has changed since the last upsert or getCrq request
const wiiHasChanges = ({
  wii,
  prevWiiData,
}) => {
  if (!prevWiiData) { return true; }

  const hasFileData = wii.attachment && wii.attachment.data;

  // If there is file data, this wii has changes
  if (hasFileData) { return true; }

  const prevWii = prevWiiData.find((prevItem) => prevItem.id === wii.id);

  // if no equivalent wii found, this is a new one
  if (!prevWii) { return true; }

  const prevToCompare = JSON.stringify(standardiseWii(prevWii));
  const wiiToCompare = JSON.stringify(standardiseWii(wii));

  const hasChanges = wiiToCompare !== prevToCompare;
  return hasChanges;
};


// Return an array of objects that contain the wii parameter data, and a boolean for
// whether the parameters have changed since the last upsert or get.
// We compare each wii with the previous wii from redux and add populate hasChanges boolean
// as true if the data has changed.
const getWiisWithHasChanges = ({
  prevWiiData,
  wiiData,
}) => (
  wiiData.map((wii) => {
    const hasChanges = wiiHasChanges({
      wii,
      prevWiiData,
    });
    return ({
      wii,
      hasChanges,
    });
  })
);

/* - Create CRQ thunk action  - */
const createWorkInfoItemsForCRQ = (crqId, wiiData) => (dispatch, getState) => {

  const prevWiiData = getState().workInfoItem.workInfoItemsByCrqId[crqId];

  const wiisWithHasChanges = getWiisWithHasChanges({
    wiiData,
    prevWiiData,
  });
  // there are no changes if all of the wiis have `hasChanges === false`
  const noChanges = !wiisWithHasChanges || wiisWithHasChanges.every((item) => !item.hasChanges);

  // if the data being sent is unchanged since last time this crq was updated
  // don't make a request.
  if (noChanges) {
    return Promise.resolve({ crqId, items: null });
  }

  // only send up the wiis that have changes as parameters
  const filteredWiis = wiisWithHasChanges
    .filter((item) => item.hasChanges)
    .map((item) => item.wii);

  // notify reducer saving has started
  dispatch(createWorkInfoItemsRequest(crqId));

  // REST POST request
  return createWorkInfoItems(filteredWiis)
    .then((items) => {
      // the server sends back ids only for the updated wiis.
      // so create a new array of wiiData, where the changed wiis have their
      // ids populated from this array
      let changedIndex = 0;
      const wiiDataWithNewIds = wiisWithHasChanges.map(({ hasChanges, wii }) => {
        if (hasChanges) {
          // if the wii had changes, return a copy with the new id
          const item = items[changedIndex];
          const id = item ? item.id : wii.id;
          // advance changedIndex to the next item in the items array
          changedIndex += 1;
          return ({
            ...wii,
            id,
          });
        }
        return wii;
      });
      // keep a record of the wii data sent to the server but populated with
      // the new ids
      dispatch(createWorkInfoItemsSuccess(crqId, wiiDataWithNewIds));
      // return the wiiData populated with the new ids
      return { crqId, workItems: wiiDataWithNewIds };
    })
    .catch((error) => {
      dispatch(createWorkInfoItemsError(crqId, error));
      // bubble promise up to the next catch
      return Promise.reject(error);
    });
};


export {
  CREATE_WORK_INFO_ITEMS_REQUEST,
  CREATE_WORK_INFO_ITEMS_SUCCESS,
  CREATE_WORK_INFO_ITEMS_ERROR,
  WORK_INFO_ITEM_ATTACHMENT_READ,
  CRQ_WORK_ITEMS_FETCHED,
  createWorkInfoItemsRequest,
  createWorkInfoItemsSuccess,
  createWorkInfoItemsError,
  createWorkInfoItemsForCRQ,
  crqWorkItemsFetched,
  workInfoItemAttachmentRead,
};
